import { ITag } from "@fluentui/react";

export const sortTerms = (input: string, data: ITag[]): ITag[] => {
    try {
        let first: ITag[] = [];
        let others: ITag[] = [];
        const sortString: string = input?.trim()?.toLowerCase();
        if (sortString) {
            for (let i = 0; i < data.length; i++) {
                if (data[i].name.toLowerCase().indexOf(sortString) === 0) {
                    first.push(data[i]);
                } else {
                    others.push(data[i]);
                }
            }
            first.sort(sortAtoZ);
            others.sort(sortAtoZ);
            return (first.concat(others));
        }
        else {
            return data.sort(sortAtoZ);
        }
    } catch (error) {
        console.warn(error);
    }
    return data;
}

const sortAtoZ = (a, b) => a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;

/**
 * The suggestion filter every datasource picker runs over its candidate tags: drop blanks, keep
 * substring matches, de-duplicate, and drop anything already selected.
 *
 * The de-duplication is a Set rather than the `tags.findIndex(t => t.name === tag.name) === index`
 * this replaces, which was O(n²) over the candidate list: ~n²/2 string comparisons on the UI
 * thread. Reached by a datasource browse AND by any loose typeahead that matches broadly, so it
 * is not bounded by the picker's own display cap - and Exact's maxRows clamps to 50 000.
 *
 * Exactly equivalent to the findIndex form. Two details are load-bearing for that:
 *  - the Set is keyed on the RAW name, never the lower-cased one. findIndex compared
 *    `t.name === tag.name`, so "ACME" and "Acme" are two distinct suggestions; keying on the
 *    lower-cased form would silently collapse them.
 *  - a name is claimed BEFORE the already-selected test, mirroring the original's `&&` order.
 *    Returning early on `selectedTags` without claiming would let a duplicate of the selected
 *    value resurface as a suggestion.
 * Both hold because every other predicate here is a pure function of the tag's value, so two tags
 * sharing a name always agree on them - the first occurrence in the array is therefore always the
 * first to reach the Set, whichever order the predicates run in.
 *
 * `matchEverything` is the callers' `getAllSuggestions` ("*" browse): the substring test is
 * skipped, nothing else is.
 */
export const filterSuggestedTagList = (
    tags: ITag[],
    filterText: string,
    selectedTags?: ITag[],
    matchEverything: boolean = false,
): ITag[] => {
    const needle: string = filterText?.trim()?.toLowerCase() ?? "";
    const seen: Set<string> = new Set<string>();
    const suggestions: ITag[] = [];

    for (const tag of tags ?? []) {
        const tagName: string = tag?.name?.toLowerCase();
        if (!tagName || tagName.length === 0) {
            continue;
        }
        if (!matchEverything && tagName.indexOf(needle) === -1) {
            continue;
        }
        if (seen.has(tag.name)) {
            continue;
        }
        seen.add(tag.name);
        // Compares key, not name - the two are the same value in every current picker, but the
        // findIndex form this replaces did dedupe on name and select-test on key.
        if (selectedTags?.some(selected => selected.key === tag.key)) {
            continue;
        }
        suggestions.push(tag);
    }

    return suggestions;
};